import React from 'react'
import { Droppable } from '@hello-pangea/dnd'
import { Box, Paper, Typography, Chip } from '@mui/material'
import { Ticket, TicketStatus } from '@shared/types/entities'
import { KanbanTicketCard } from './KanbanTicketCard'

interface KanbanColumnProps {
  status: TicketStatus
  tickets: Ticket[]
  onTicketClick: (ticketId: string) => void
}

const getStatusLabel = (status: TicketStatus): string => {
  switch (status) {
    case 'Open':
      return 'Open'
    case 'InProgress':
      return 'In Progress'
    case 'WaitingOnThirdParty':
      return 'Waiting on Third Party'
    case 'Resolved':
      return 'Resolved'
    case 'Closed':
      return 'Closed'
    default:
      return status
  }
}

const getStatusColor = (status: TicketStatus): 'default' | 'warning' | 'info' | 'success' => {
  switch (status) {
    case 'Open':
      return 'warning'
    case 'InProgress':
      return 'info'
    case 'WaitingOnThirdParty':
      return 'warning'
    case 'Resolved':
      return 'success'
    case 'Closed':
      return 'default'
    default:
      return 'default'
  }
}

export const KanbanColumn: React.FC<KanbanColumnProps> = ({
  status,
  tickets,
  onTicketClick
}) => {
  return (
    <Paper
      elevation={1}
      sx={{
        width: 300,
        minWidth: 300,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: 'grey.50'
      }}
    >
      {/* Column Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Chip
            label={getStatusLabel(status)}
            color={getStatusColor(status)}
            size="small"
            sx={{ fontWeight: 'bold' }}
          />
          <Typography variant="body2" color="text.secondary">
            {tickets.length}
          </Typography>
        </Box>
      </Box>

      {/* Droppable Area */}
      <Droppable droppableId={status}>
        {(provided, snapshot) => (
          <Box
            ref={provided.innerRef}
            {...provided.droppableProps}
            sx={{
              p: 1.5,
              flexGrow: 1,
              overflowY: 'auto',
              minHeight: 200,
              backgroundColor: snapshot.isDraggingOver ? 'action.hover' : 'grey.50',
              transition: 'background-color 0.2s ease'
            }}
          >
            {tickets.map((ticket, index) => (
              <KanbanTicketCard
                key={ticket.ticketId}
                ticket={ticket}
                index={index}
                onClick={onTicketClick}
              />
            ))}
            {provided.placeholder}

            {/* Empty State */}
            {tickets.length === 0 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 200,
                  color: 'text.secondary'
                }}
              >
                <Typography variant="body2">
                  No tickets
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Droppable>
    </Paper>
  )
}
